import React from 'react';

import { Layout } from '@components/Layout';
import { QueryContainer } from '@components/QueryContainer';
import { Container } from '@components/utilities/Contact/Container';
import { FriendInvitationRoles, InvitationItem } from '@components/utilities/Friend/InvitationItem';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { contactRequestStrings } from '@data/contacts';
import { useCompany } from '@hooks/queries/useCompany';
import { useFriendInvitedList } from '@hooks/queries/useFriendInvitedList';
import { useFriendInvitingList } from '@hooks/queries/useFriendInvitingList';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { type AppUrl, url } from '@utils/pages';
import clsx from 'clsx';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const searchType = t.type({
  from: h.string(),
  contactType: h.omittable(h.string()),
});

const strings = createStrings({
  ja: {
    title: (pageTitle: string) => `${pageTitle}一覧`,
    noRequest: '連携リクエストはありません。',
    prompt: (type: string) => (
      <div>
        {`下記リクエストを承認すると、あなたは送信先の${type}一覧に追加されます。ユーザーの個人情報を保護するため、連携リクエストを送信した方をあなたの SHIPPER（輸出者）一覧やCNEE（輸入者）一覧に自動的に追加することはできません。連携リクエストを送信した人をあなたの`}
        SHIPPER（輸出者）一覧や CNEE（輸入者）一覧 に追加したい場合は、{' '}
        <a href={url('/utilities/Contact/Shippers/Shippers')}>SHIPPER（輸出者）一覧</a> や{' '}
        <a href={url('/utilities/Contact/Consignees/Consignees')}>CNEE（輸入者）一覧</a>{' '}
        ページの「+D2D MEMBERS」機能から新たな連携リクエストを送ってください。
      </div>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  requestListContainer: {
    ...common.flexColumn,
    overflow: 'auto',
    rowGap: 10,
    maxWidth: '900px',
    [theme.breakpoints.down('xs')]: {
      height: '23vh',
    },
  },
  prompt: {
    ...common.smallerText,
    padding: '10px',
    lineHeight: '1.125rem',
    '& a': {
      cursor: 'pointer',
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
}));

interface InvitationListProps {
  role: FriendInvitationRoles;
}

export const InvitationList: React.FC<InvitationListProps> = (props) => {
  const { role } = props;
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  const { search } = useSearch(searchType);

  const invitingList = useFriendInvitingList({
    contact_type: search.contactType ?? undefined,
  });
  const invitedList = useFriendInvitedList();
  const company = useCompany();

  const requests = role === 'requester' ? invitingList : invitedList;

  const isUserShipper = company.data?.country === 'cn';

  const pageTitle =
    role === 'recipient'
      ? contactRequestStrings.requestsToConnectReceived
      : search.contactType === 'shipper'
      ? contactRequestStrings.requestsToConnectShipperSent
      : contactRequestStrings.requestsToConnectConsigneeSent;

  return (
    <Layout
      title={pageTitle}
      loading={false}
      errors={[]}
      breadcrumbs={[
        '/utilities/Dashboard',
        search.from as AppUrl,
        {
          text: strings.title(pageTitle),
        },
      ]}
    >
      {() => (
        <div className={classes.body}>
          <Container>
            <VerticalSpace height={60} />

            <div className={clsx(classes.pageTitleText, classes.textPrimary)}>{pageTitle}</div>

            <VerticalSpace height={role === 'recipient' && requests.data?.length !== 0 ? 10 : 47} />

            <QueryContainer queries={[invitingList, invitedList, company]}>
              <div className={classes.requestListContainer}>
                {requests.data?.length === 0 ? (
                  <div className={classes.textGray500}>{strings.noRequest}</div>
                ) : (
                  <>
                    {role === 'recipient' && (
                      <div className={classes.prompt}>
                        {strings.prompt(
                          isUserShipper
                            ? bookingInquiryStrings.shipper
                            : bookingInquiryStrings.consignee,
                        )}
                      </div>
                    )}
                    {requests.data?.map((request) => (
                      <InvitationItem
                        id={request.id}
                        contact={request.company}
                        status={request.status}
                        role={role}
                        key={request.id}
                      />
                    ))}
                  </>
                )}
              </div>
            </QueryContainer>
            <VerticalSpace height={70} />
          </Container>
        </div>
      )}
    </Layout>
  );
};
